import { useQuery } from "@tanstack/react-query";
import { RefreshCw, MessageSquare, CheckCircle2, XCircle, Clock } from "lucide-react";
import { api } from "@/lib/api";

const STATUS = {
  delivered: { label: "Delivered", cls: "bg-[#2D6A4F]/10 text-[#2D6A4F]", Icon: CheckCircle2 },
  failed: { label: "Failed", cls: "bg-[#9B2226]/10 text-[#9B2226]", Icon: XCircle },
  pending: { label: "Pending", cls: "bg-goldLight text-espresso", Icon: Clock },
};

const fmt = (iso) => new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZone: "America/New_York" }) + " ET";

function Row({ it }) {
  const s = STATUS[it.status] || STATUS.pending;
  return (
    <div className="rounded-2xl bg-white border border-hair p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between" data-testid={`text-row-${it.rsvp_id}`}>
      <div className="min-w-0">
        <p className="font-semibold text-espresso">{it.name}</p>
        <p className="text-sm text-inkSoft">{it.phone_display} · Sent {fmt(it.sent_at)}</p>
        {it.reason && <p className="text-sm text-[#9B2226] mt-1" data-testid={`text-reason-${it.rsvp_id}`}>{it.reason}</p>}
      </div>
      <span className={`self-start sm:self-center px-3 py-1.5 rounded-full text-sm font-semibold flex items-center gap-1.5 ${s.cls}`} data-testid={`text-status-${it.rsvp_id}`}>
        <s.Icon size={14} /> {s.label}
      </span>
    </div>
  );
}

export function TextsTab() {
  const { data, isFetching, refetch } = useQuery({ queryKey: ["host-reminder-report"], queryFn: async () => (await api.get("/host/reminder-report")).data });
  const sum = data?.summary;
  return (
    <div className="space-y-6 animate-fade-in max-w-3xl">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl text-espresso">Reminder Texts</h1>
          <p className="text-inkSoft mt-1">Delivery status for the most recent reminder send.</p>
        </div>
        <button onClick={() => refetch()} disabled={isFetching} className="flex items-center gap-2 rounded-full border border-hair bg-white px-4 py-2.5 text-sm font-semibold text-espresso hover:border-gold disabled:opacity-50 transition-colors" data-testid="texts-refresh">
          <RefreshCw size={15} className={isFetching ? "animate-spin" : ""} /> Refresh
        </button>
      </div>
      {!data?.items?.length ? (
        <div className="rounded-3xl bg-white border border-hair shadow-soft p-10 text-center text-inkSoft" data-testid="texts-empty">
          <MessageSquare className="mx-auto text-inkMuted mb-3" /> No reminders sent yet. Use "Send Reminder Now" on the Overview tab.
        </div>
      ) : (
        <>
          <p className="text-espresso font-semibold" data-testid="texts-summary">
            {sum.delivered} delivered · {sum.failed} failed · {sum.pending} pending
            <span className="text-inkMuted font-normal"> — sent {fmt(data.sent_at)}</span>
          </p>
          <div className="space-y-3">{data.items.map((it) => <Row key={it.id} it={it} />)}</div>
        </>
      )}
    </div>
  );
}
